export type CoachingRole = "super_admin" | "admin" | "coach" | "company_admin" | "participant" | "cohort_lead";

export type RoleScope =
  | "global"
  | "assigned-coach"
  | "single-company"
  | "own-learner-records"
  | "assigned-cohorts";

export type RoleExperienceMode =
  | "global-operations"
  | "assigned-operations"
  | "company-read-only"
  | "learner-private"
  | "cohort-lead-read-only";

export type RoleDataSurface =
  | "global-operational-records"
  | "assigned-coach-records"
  | "assigned-company-summary"
  | "own-learner-records"
  | "assigned-cohort-summary";

export interface RoleDefinition {
  role: CoachingRole;
  label: string;
  scope: RoleScope;
  writePosture: "full" | "operational" | "scoped" | "read-only";
  implementationStage: "active" | "planned";
  experienceMode: RoleExperienceMode;
  dataSurface: RoleDataSurface;
  defaultRouteId: string;
  canGrantRoles: CoachingRole[];
}

export const roleDefinitions: RoleDefinition[] = [
  {
    role: "super_admin",
    label: "Super admin",
    scope: "global",
    writePosture: "full",
    implementationStage: "planned",
    experienceMode: "global-operations",
    dataSurface: "global-operational-records",
    defaultRouteId: "dashboard",
    canGrantRoles: ["super_admin", "admin", "coach", "company_admin", "cohort_lead", "participant"]
  },
  {
    role: "admin",
    label: "Admin",
    scope: "global",
    writePosture: "operational",
    implementationStage: "active",
    experienceMode: "global-operations",
    dataSurface: "global-operational-records",
    defaultRouteId: "dashboard",
    canGrantRoles: ["coach", "company_admin", "cohort_lead", "participant"]
  },
  {
    role: "coach",
    label: "Coach",
    scope: "assigned-coach",
    writePosture: "scoped",
    implementationStage: "active",
    experienceMode: "assigned-operations",
    dataSurface: "assigned-coach-records",
    defaultRouteId: "my-cohorts",
    canGrantRoles: []
  },
  {
    // Phase 4 (org-admin spec §2.1): promoted to active with a SCOPED
    // follow-through write posture. scope stays single-company (no data
    // widening); writePosture:"scoped" exists only so the capability layer can
    // flip the two follow-through actions (reports:mark-reviewed, exports:csv)
    // to enabled. All other mutations stay denied via per-capability
    // readOnlyRoles membership (NOT a role-wide read-only flag).
    role: "company_admin",
    label: "Company admin",
    scope: "single-company",
    writePosture: "scoped",
    implementationStage: "active",
    experienceMode: "company-read-only",
    dataSurface: "assigned-company-summary",
    defaultRouteId: "org-admin-home",
    canGrantRoles: []
  },
  {
    role: "participant",
    label: "Participant",
    scope: "own-learner-records",
    writePosture: "read-only",
    implementationStage: "planned",
    experienceMode: "learner-private",
    dataSurface: "own-learner-records",
    defaultRouteId: "dashboard",
    canGrantRoles: []
  },
  {
    // F7 (Z5 backend cohort_lead role): VIEW + report-download only, scoped to
    // assigned cohort(s). The backend cohort_lead scope mode denies every
    // mutation; the frontend never renders mutation controls for this role
    // (read-only writePosture keeps the capability layer at disabled/hidden).
    role: "cohort_lead",
    label: "Cohort lead",
    scope: "assigned-cohorts",
    writePosture: "read-only",
    implementationStage: "active",
    experienceMode: "cohort-lead-read-only",
    dataSurface: "assigned-cohort-summary",
    defaultRouteId: "dashboard",
    canGrantRoles: []
  }
];

export const allCoachingRoles = roleDefinitions.map((definition) => definition.role);

export function getRoleDefinition(role: CoachingRole) {
  return roleDefinitions.find((definition) => definition.role === role) ?? roleDefinitions[1];
}

export function isCoachingRole(value: unknown): value is CoachingRole {
  return typeof value === "string" && allCoachingRoles.includes(value as CoachingRole);
}

export function normalizeCoachingRole(value: unknown): CoachingRole {
  return isCoachingRole(value) ? value : "admin";
}

export function canGrantRole(granter: CoachingRole, target: CoachingRole) {
  return getRoleDefinition(granter).canGrantRoles.includes(target);
}

export function isReadOnlyRole(role: CoachingRole) {
  return getRoleDefinition(role).writePosture === "read-only";
}

/**
 * Whether the role is read-only on OPERATIONAL surfaces (companies, programs,
 * cohorts, sessions, participants). company_admin is promoted to "scoped" write
 * posture for the two follow-through capabilities (mark-reviewed, exports:csv),
 * but it stays read-only on every operational surface — so operational surfaces
 * gate their read-only copy on this helper, not the raw writePosture.
 */
export function isOperationalReadOnlyRole(role: CoachingRole) {
  return isReadOnlyRole(role) || role === "company_admin";
}
